import { TrustSignals } from '@/components/marketing/TrustSignals';
import { ValueProposition } from '@/components/marketing/ValueProposition';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { safeLocalStorage } from '@/lib/safeStorage';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { LAST_LOGIN_EMAIL_STORAGE_KEY, useAuth } from '../contexts/AuthContext';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof formSchema>;

const Login = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Read once per mount (idempotent, so StrictMode-safe). Tracked as state so
  // the "Not you?" escape hatch can clear the pre-fill and hide itself.
  const [rememberedEmail, setRememberedEmail] = useState(
    () => safeLocalStorage.getItem(LAST_LOGIN_EMAIL_STORAGE_KEY) ?? ''
  );
  const navigate = useNavigate();
  const { login } = useAuth();

  // Initialize form with React Hook Form and specify the exact type
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // Pre-fill the email remembered from the last successful login on this
      // device; the password always comes from the user (or their browser's
      // password manager).
      email: rememberedEmail,
      password: '',
    },
  });

  // Escape hatch for shared workstations: forget the previous user's email
  // and clear the pre-filled field.
  const forgetRememberedEmail = () => {
    safeLocalStorage.removeItem(LAST_LOGIN_EMAIL_STORAGE_KEY);
    setRememberedEmail('');
    form.setValue('email', '');
    form.setFocus('email');
  };

  const onSubmit = async (values: FormValues) => {
    setError('');
    setIsLoading(true);

    try {
      const user = await login({
        email: values.email,
        password: values.password,
      });
      // Check if email verification is required — redirect explicitly.
      // For all other cases, PublicOnlyRoute handles the redirect once
      // isAuthenticated becomes true, avoiding a race condition where
      // navigate() and PublicOnlyRoute's <Navigate> would conflict.
      // Uses the server-computed predicate (Project C PR2), so a grace-window
      // user is not bounced — identical to the pre-PR2 `null` behavior.
      if (user.emailVerificationRequired) {
        navigate('/verify-email', { replace: true });
      }
    } catch (error: unknown) {
      setError(
        error instanceof Error
          ? error.message
          : 'Login failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-12 min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Heading: visible on mobile, hidden on desktop (left panel has the branding) */}
        <h1 className="text-marketing-40 mb-6 lg:hidden">Provider Portal</h1>

        {/* 2-column layout: Value Prop + Trust (left) | Form (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(400px,500px)] gap-12 items-start">
          {/* Left: Brand messaging + Value Proposition + Trust Signals (hidden on mobile) */}
          <div className="hidden lg:flex lg:flex-col gap-8 sticky top-24">
            <div>
              <p className="text-marketing-18 mb-2">Providers &amp; Clinics</p>
              <h2 className="text-marketing-40 mb-4">Provider Portal</h2>
              <p className="text-long-secondary text-lg">
                Sign in to send orders, request medical records, and manage your
                referrals — all from one place.
              </p>
            </div>
            <ValueProposition
              heading="Your Referral Management Hub by Expected Health"
              bullets={[
                'Send and track orders in real time',
                'Request and view results automatically',
                'Manage medical record requests',
              ]}
            />
            <TrustSignals hideTestimonial />
          </div>

          {/* Right: Login form card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
            <p className="text-body mb-8">
              Sign into your account or{' '}
              <a href="/register" className="text-link underline">
                create a new account
              </a>
              .
            </p>

            {error ? (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel className="text-body-small-strong">
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="you@example.com"
                          type="email"
                          autoComplete="username"
                          size="lg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      {rememberedEmail ? (
                        <button
                          type="button"
                          onClick={forgetRememberedEmail}
                          className="text-caption-label text-link hover:underline"
                        >
                          Not you? Use a different email
                        </button>
                      ) : null}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel className="text-body-small-strong">
                        Password
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          autoComplete="current-password"
                          size="lg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end mb-8">
                  <a
                    href="/forgot-password"
                    className="text-body-small text-link hover:underline"
                  >
                    Forgot your password?
                  </a>
                </div>

                <Button
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                  className="w-full mb-8 h-13 text-lg"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </Form>
          </div>
        </div>

        {/* Mobile: Show value prop and trust signals below form */}
        <div className="lg:hidden mt-8 space-y-6">
          <ValueProposition />
          <TrustSignals hideTestimonial />
        </div>
      </div>
    </div>
  );
};

export default Login;
